/**
 * ExcelWidget — Market Monitor "Excel" widget body.
 *
 * Selection model (Excel-like):
 *   - Single click selects a cell (no edit).
 *   - Shift+click extends from anchor to the clicked cell (rectangle).
 *   - Ctrl/Cmd+click toggles a single cell in the selection.
 *   - Double click (or Enter / F2 / typing) on a selected cell enters edit.
 *   - Delete / Backspace clears the value of all selected cells.
 *
 * DSL evaluation: cells whose `raw` starts with "=" go through the
 * NL→DSL→executeDsl pipeline (see useExcelEvaluations); the cell's
 * unfocused face shows the result, while edit mode flips it to an input
 * bound to `raw`.
 *
 * Cell formatting: `textColor` and `bgColor` are persisted per-cell via
 * controller.setCellsStyle; the toolbar exposes two color pickers that
 * apply the selected color to all selected cells.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Plus,
  Minus,
  RefreshCw,
  Trash2,
  Type,
  PaintBucket,
  Undo2,
  RotateCcw,
  LineChart,
  Hash,
  Wand2,
  Pin,
  PinOff,
  Sparkles,
  Upload,
} from 'lucide-react';

import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import {
  isDslCell,
  isExcelConfig,
  isSheetEmpty,
  MAX_DECIMAL_DIGITS,
  MAX_EXCEL_COLS,
  MAX_EXCEL_ROWS,
  MIN_DECIMAL_DIGITS,
  promptOf,
  sheetCellMap,
  type CellCoord,
  type ExcelCellNumberFormat,
  type ExcelWidgetConfig,
} from './excelConfig';
import { formulaMode } from './excelFormula';
import { memoEvaluateRules, type CfRule } from './conditionalFormatting';
import { ConditionalFormattingPopover } from './ConditionalFormattingPopover';
import { ExcelPromptPopover } from './ExcelPromptPopover';
import { ExcelUploadModal } from './ExcelUploadModal';
import { portfolioSheetCells } from './portfolioSheet';
import type { PortfolioSnapshot } from '../../../services/PortfolioImageService';
import { editExcelWidget } from '../../../services/ExcelWidgetAgentService';
import { useExcelController } from './ExcelController';
import {
  numericValueOf,
  parseLiteralNumber,
  useExcelEvaluations,
  useExcelLiveOverlay,
  type EvaluationStatus,
  type ExcelLiveOverlay,
} from './excelEvaluator';
import styles from './ExcelWidget.module.css';

const FALLBACK_CONFIG: ExcelWidgetConfig = {
  sheetId: '',
  rows: 1,
  cols: 1,
  cells: [[{ raw: '' }]],
};

const COLOR_PALETTE: readonly string[] = [
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#374151', '#000000',
  '#dc2626', '#ea580c', '#d97706', '#65a30d', '#2563eb', '#7c3aed',
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#3b82f6', '#a78bfa',
  '#fca5a5', '#fdba74', '#fcd34d', '#bef264', '#93c5fd', '#c4b5fd',
  '#fee2e2', '#ffedd5', '#fef3c7', '#ecfccb', '#dbeafe', '#ede9fe',
];

const DECIMAL_DIGIT_OPTIONS: readonly number[] = [0, 1, 2, 3, 4];

const coordKey = (r: number, c: number): string => `${r}:${c}`;

// The grid owns its own cell-selection model, and cell mousedown calls
// preventDefault — which also suppresses the browser's default clearing of the
// document selection. Drop it explicitly so no stray text selection can be
// left painted across the widget.
const dropNativeSelection = (): void => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) selection.removeAllRanges();
};

/** 0 → "A", 25 → "Z", 26 → "AA", 27 → "AB", … (A1-style column letter). */
const colName = (c: number): string => {
  let n = c;
  let out = '';
  while (n >= 0) {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
};

const formatPercent = (n: number, digits = 2): string =>
  `${(n * 100).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;

const formatDecimal = (n: number, digits: number): string =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const formatDate = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return text;
  const ms = Date.parse(trimmed);
  if (Number.isNaN(ms)) return text;
  return new Date(ms).toLocaleDateString();
};

/**
 * Apply a cell's `numberFormat` to a display string. Returns the input
 * unchanged when the format does not apply (e.g. percent on a non-
 * numeric value). The empty placeholder dash `—` is returned as-is.
 */
const applyNumberFormat = (
  text: string,
  format: ExcelCellNumberFormat | undefined,
): string => {
  if (!format) return text;
  if (text === '' || text === '—') return text;
  switch (format.kind) {
    case 'percent': {
      const n = parseLiteralNumber(text);
      return n === null ? text : formatPercent(n);
    }
    case 'decimal': {
      const n = parseLiteralNumber(text);
      return n === null ? text : formatDecimal(n, format.digits);
    }
    case 'text':
      return text;
    case 'date':
      return formatDate(text);
    default:
      return text;
  }
};

const describeFormat = (format: ExcelCellNumberFormat | undefined): string => {
  if (!format) return 'None';
  switch (format.kind) {
    case 'percent':
      return 'Percent';
    case 'decimal':
      return `Decimal (${format.digits})`;
    case 'text':
      return 'Text';
    case 'date':
      return 'Date';
  }
};

const formatsEqual = (
  a: ExcelCellNumberFormat | undefined,
  b: ExcelCellNumberFormat | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'decimal' && b.kind === 'decimal') {
    return a.digits === b.digits;
  }
  return true;
};

const sharedFormat = (
  cells: { numberFormat?: ExcelCellNumberFormat }[],
): ExcelCellNumberFormat | null | undefined => {
  if (cells.length === 0) return undefined;
  const first = cells[0].numberFormat;
  for (let i = 1; i < cells.length; i += 1) {
    if (!formatsEqual(first, cells[i].numberFormat)) return undefined;
  }
  return first ?? null;
};

const rangeBetween = (a: CellCoord, b: CellCoord): CellCoord[] => {
  const r0 = Math.min(a.r, b.r);
  const r1 = Math.max(a.r, b.r);
  const c0 = Math.min(a.c, b.c);
  const c1 = Math.max(a.c, b.c);
  const out: CellCoord[] = [];
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) out.push({ r, c });
  }
  return out;
};

type CellExitReason = 'commit' | 'cancel' | 'blur';

interface CellViewProps {
  r: number;
  c: number;
  raw: string;
  isDsl: boolean;
  evaluation: EvaluationStatus | undefined;
  liveOverlay: ExcelLiveOverlay | undefined;
  editing: boolean;
  initialValueOverride: string | null;
  textColor?: string;
  numberFormat?: ExcelCellNumberFormat;
  onCommit: (r: number, c: number, raw: string) => void;
  onExitEdit: (reason: CellExitReason) => void;
}

const ExcelCellView: React.FC<CellViewProps> = ({
  r,
  c,
  raw,
  isDsl,
  evaluation,
  liveOverlay,
  editing,
  initialValueOverride,
  textColor,
  numberFormat,
  onCommit,
  onExitEdit,
}) => {
  const [value, setValue] = useState(raw);
  const valueRef = useRef(raw);
  const committedRef = useRef(raw);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasEditingRef = useRef(editing);
  const overrideRef = useRef(initialValueOverride);

  useEffect(() => {
    overrideRef.current = initialValueOverride;
  }, [initialValueOverride]);

  useEffect(() => {
    committedRef.current = raw;
    valueRef.current = raw;
    setValue(raw);
  }, [raw]);

  const updateValue = useCallback((next: string) => {
    valueRef.current = next;
    setValue(next);
  }, []);

  const commit = useCallback(() => {
    if (valueRef.current === committedRef.current) return;
    committedRef.current = valueRef.current;
    onCommit(r, c, valueRef.current);
  }, [r, c, onCommit]);

  const revert = useCallback(() => {
    updateValue(committedRef.current);
  }, [updateValue]);

  useEffect(() => {
    if (editing && !wasEditingRef.current && inputRef.current) {
      const override = overrideRef.current;
      if (override != null) {
        committedRef.current = raw;
        valueRef.current = override;
        setValue(override);
        inputRef.current.focus();
        const len = override.length;
        requestAnimationFrame(() => {
          if (inputRef.current) inputRef.current.setSelectionRange(len, len);
        });
      } else {
        inputRef.current.focus();
        inputRef.current.select();
      }
    } else if (!editing && wasEditingRef.current) {
      commit();
    }
    wasEditingRef.current = editing;
  }, [editing, commit, raw]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      onExitEdit('commit');
      return;
    }
    if (event.key === 'Tab') {
      commit();
      onExitEdit('commit');
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      revert();
      onExitEdit('cancel');
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={`${styles.cellInput} ${
          isDsl ? styles.dslCell : styles.literalCell
        }`}
        style={textColor ? { color: textColor } : undefined}
        value={value}
        data-testid={`excel-cell-${r}-${c}`}
        onChange={(event) => updateValue(event.target.value)}
        onBlur={() => {
          commit();
          onExitEdit('blur');
        }}
        onKeyDown={handleKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      />
    );
  }

  const mode = isDsl ? formulaMode(promptOf({ raw })) : null;

  let display: string;
  let className = styles.cellDisplay;
  let title = '';
  let canFormat = false;
  switch (evaluation?.status) {
    case 'waiting':
      display = '';
      className += ' ' + styles.loading;
      title = 'Waiting for a referenced cell…';
      break;
    case 'translating':
      display = '';
      className += ' ' + styles.loading;
      title = 'Translating prompt…';
      break;
    case 'executing':
      display = '';
      className += ' ' + styles.loading;
      title = `Executing: ${evaluation.resolvedDsl}`;
      break;
    case 'ok':
      display = liveOverlay ? liveOverlay.display : evaluation.display;
      title = `Resolved: ${evaluation.resolvedDsl}`;
      if (mode === 'deterministic') title += '\nDeterministic — no AI used.';
      canFormat = true;
      break;
    case 'error':
      display =
        evaluation.stage === 'translate' ? '#TRANSLATE_ERR' : '#ERROR';
      className += ' ' + styles.error;
      title = evaluation.message;
      break;
    case 'idle':
    default:
      display = raw || '';
      if (!raw) className += ' ' + styles.empty;
      else canFormat = !isDsl;
      break;
  }

  if (canFormat) display = applyNumberFormat(display, numberFormat);

  return (
    <div
      className={className}
      style={textColor ? { color: textColor } : undefined}
      data-testid={`excel-cell-${r}-${c}`}
      data-cell-mode={mode ?? undefined}
      title={title}
    >
      {liveOverlay ? (
        <span key={liveOverlay.tickEpoch} className={styles.tickFlash}>
          {display}
        </span>
      ) : (
        display
      )}
    </div>
  );
};


export const ExcelWidget: React.FC<WidgetInstanceProps> = ({
  instanceId,
  config,
}) => {
  const controller = useExcelController(instanceId);

  const safeConfig: ExcelWidgetConfig = isExcelConfig(config)
    ? (config as unknown as ExcelWidgetConfig)
    : FALLBACK_CONFIG;

  const handleResolved = useCallback(
    (r: number, c: number, dsl: string) => {
      controller.setResolvedDsl(r, c, dsl);
    },
    [controller],
  );

  const evaluations = useExcelEvaluations(safeConfig, {
    onResolved: handleResolved,
    refreshEpoch: controller.refreshEpoch,
    retranslateEpoch: controller.retranslateEpoch,
  });

  const liveOverlay = useExcelLiveOverlay(instanceId, evaluations);

  const handleCellCommit = useCallback(
    (r: number, c: number, raw: string) => {
      controller.setCellRaw(r, c, raw);
    },
    [controller],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<CellCoord | null>(null);
  const [active, setActive] = useState<CellCoord | null>(null);
  const [editingCell, setEditingCell] = useState<CellCoord | null>(null);
  const [editingInitialValue, setEditingInitialValue] = useState<string | null>(
    null,
  );
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const promptButtonRef = useRef<HTMLButtonElement | null>(null);
  const [promptBusy, setPromptBusy] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const promptAbortRef = useRef<AbortController | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!active || !gridRef.current) return;
    const cell = gridRef.current.querySelector(
      `[data-testid="excel-td-${active.r}-${active.c}"]`,
    );
    if (cell instanceof HTMLElement && typeof cell.scrollIntoView === 'function') {
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [active]);

  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((key) => {
        const [rs, cs] = key.split(':');
        const r = Number(rs);
        const c = Number(cs);
        if (r < safeConfig.rows && c < safeConfig.cols) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
    setEditingCell((prev) => {
      if (!prev) return prev;
      if (prev.r >= safeConfig.rows || prev.c >= safeConfig.cols) return null;
      return prev;
    });
    setAnchor((prev) => {
      if (!prev) return prev;
      if (prev.r >= safeConfig.rows || prev.c >= safeConfig.cols) return null;
      return prev;
    });
    setActive((prev) => {
      if (!prev) return prev;
      if (prev.r >= safeConfig.rows || prev.c >= safeConfig.cols) return null;
      return prev;
    });
  }, [safeConfig.rows, safeConfig.cols]);

  const selectedCoords = useMemo<CellCoord[]>(() => {
    const out: CellCoord[] = [];
    selected.forEach((key) => {
      const [rs, cs] = key.split(':');
      out.push({ r: Number(rs), c: Number(cs) });
    });
    return out;
  }, [selected]);

  const hasSelection = selected.size > 0;
  const dslSelectedCoords = useMemo<CellCoord[]>(
    () =>
      selectedCoords.filter(({ r, c }) => {
        if (r < 0 || r >= safeConfig.rows) return false;
        if (c < 0 || c >= safeConfig.cols) return false;
        return isDslCell(safeConfig.cells[r][c]);
      }),
    [selectedCoords, safeConfig],
  );
  const hasDslSelection = dslSelectedCoords.length > 0;

  const handleCellMouseDown = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>, r: number, c: number) => {
      if (editingCell && editingCell.r === r && editingCell.c === c) return;
      event.preventDefault();
      gridRef.current?.focus({ preventScroll: true });
      const key = coordKey(r, c);
      setEditingCell(null);
      setEditingInitialValue(null);
      if (event.shiftKey && anchor) {
        const coords = rangeBetween(anchor, { r, c });
        setSelected(new Set(coords.map(({ r: rr, c: cc }) => coordKey(rr, cc))));
        setActive({ r, c });
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
        setAnchor({ r, c });
        setActive({ r, c });
        return;
      }
      setSelected(new Set([key]));
      setAnchor({ r, c });
      setActive({ r, c });
    },
    [anchor, editingCell],
  );

  const handleCellDoubleClick = useCallback(
    (r: number, c: number) => {
      setEditingCell({ r, c });
      setEditingInitialValue(null);
      setSelected(new Set([coordKey(r, c)]));
      setAnchor({ r, c });
      setActive({ r, c });
    },
    [],
  );

  const handleSelectAllCells = useCallback(() => {
    const keys: string[] = [];
    for (let r = 0; r < safeConfig.rows; r += 1) {
      for (let c = 0; c < safeConfig.cols; c += 1) keys.push(coordKey(r, c));
    }
    setSelected(new Set(keys));
    setAnchor({ r: 0, c: 0 });
    setActive({ r: 0, c: 0 });
  }, [safeConfig.rows, safeConfig.cols]);

  const exitEdit = useCallback((reason: CellExitReason) => {
    setEditingCell(null);
    setEditingInitialValue(null);
    if (reason !== 'blur') {
      gridRef.current?.focus({ preventScroll: true });
    }
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selectedCoords.length === 0) return;
    controller.clearCells(selectedCoords);
  }, [controller, selectedCoords]);

  const handleTextColor = useCallback(
    (color: string) => {
      if (selectedCoords.length === 0) return;
      controller.setCellsStyle(selectedCoords, { textColor: color });
    },
    [controller, selectedCoords],
  );

  const handleBgColor = useCallback(
    (color: string) => {
      if (selectedCoords.length === 0) return;
      controller.setCellsStyle(selectedCoords, { bgColor: color });
    },
    [controller, selectedCoords],
  );

  const handleClearFormatting = useCallback(() => {
    if (selectedCoords.length === 0) return;
    controller.setCellsStyle(selectedCoords, {
      textColor: null,
      bgColor: null,
    });
  }, [controller, selectedCoords]);

  const handleResetTextColor = useCallback(() => {
    if (selectedCoords.length === 0) return;
    controller.setCellsStyle(selectedCoords, { textColor: null });
  }, [controller, selectedCoords]);

  const handleResetBgColor = useCallback(() => {
    if (selectedCoords.length === 0) return;
    controller.setCellsStyle(selectedCoords, { bgColor: null });
  }, [controller, selectedCoords]);

  const handleSetFormat = useCallback(
    (format: ExcelCellNumberFormat) => {
      if (selectedCoords.length === 0) return;
      controller.setCellsFormat(selectedCoords, format);
    },
    [controller, selectedCoords],
  );

  const handleClearFormat = useCallback(() => {
    if (selectedCoords.length === 0) return;
    controller.setCellsFormat(selectedCoords, null);
  }, [controller, selectedCoords]);

  const selectedFormat = useMemo<
    ExcelCellNumberFormat | null | undefined
  >(() => {
    if (selectedCoords.length === 0) return undefined;
    const cells = selectedCoords
      .filter(({ r, c }) => r < safeConfig.rows && c < safeConfig.cols)
      .map(({ r, c }) => safeConfig.cells[r][c]);
    return sharedFormat(cells);
  }, [selectedCoords, safeConfig]);

  const cfSelection = useMemo<{
    rules: CfRule[];
    isMixed: boolean;
  }>(() => {
    if (selectedCoords.length === 0) return { rules: [], isMixed: false };
    const lists = selectedCoords
      .filter(({ r, c }) => r < safeConfig.rows && c < safeConfig.cols)
      .map(({ r, c }) => safeConfig.cells[r][c].conditionalFormatting ?? []);
    if (lists.length === 0) return { rules: [], isMixed: false };
    const first = lists[0];
    const firstKey = JSON.stringify(first);
    for (let i = 1; i < lists.length; i += 1) {
      if (JSON.stringify(lists[i]) !== firstKey) {
        return { rules: [], isMixed: true };
      }
    }
    return { rules: first, isMixed: false };
  }, [selectedCoords, safeConfig]);

  const [cfPopover, setCfPopover] = useState<{ x: number; y: number } | null>(
    null,
  );

  const handleCfApply = useCallback(
    (rules: CfRule[]) => {
      if (selectedCoords.length === 0) {
        setCfPopover(null);
        return;
      }
      controller.setCellsConditionalFormatting(
        selectedCoords,
        rules.length === 0 ? null : rules,
      );
      setCfPopover(null);
    },
    [controller, selectedCoords],
  );

  const handleCfCancel = useCallback(() => setCfPopover(null), []);

  const closePrompt = useCallback(() => {
    promptAbortRef.current?.abort();
    promptAbortRef.current = null;
    setPromptBusy(false);
    setPromptError(null);
    setPromptOpen(false);
  }, []);

  /**
   * Send the sheet and one instruction to the excel widget agent, then write
   * back only the cells it returns. The box stays open on failure so the
   * instruction can be reworded and tried again.
   */
  const handlePromptSubmit = useCallback(
    async (instruction: string) => {
      const abort = new AbortController();
      promptAbortRef.current = abort;
      setPromptBusy(true);
      setPromptError(null);
      try {
        const edit = await editExcelWidget(
          instruction,
          {
            rows: safeConfig.rows,
            cols: safeConfig.cols,
            cells: sheetCellMap(safeConfig),
          },
          abort.signal,
        );
        console.log('[excel widget agent] instruction:', instruction);
        console.log('[excel widget agent] grid:', `${edit.rows} x ${edit.cols}`);
        console.log('[excel widget agent] cells returned:', edit.cells);
        controller.applyCellPatch(edit.cells, { rows: edit.rows, cols: edit.cols });
        setPromptOpen(false);
      } catch (caught) {
        if (caught instanceof Error && caught.name === 'AbortError') return;
        console.log('[excel widget agent] instruction:', instruction);
        console.log('[excel widget agent] failed:', caught);
        setPromptError(
          caught instanceof Error
            ? caught.message
            : "Couldn't complete the task correctly. Try again!",
        );
      } finally {
        promptAbortRef.current = null;
        setPromptBusy(false);
      }
    },
    [safeConfig, controller],
  );

  const handleUndo = useCallback(() => {
    if (!controller.canUndo) return;
    controller.undo();
  }, [controller]);

  const handleChart = useCallback(() => {
    if (dslSelectedCoords.length === 0) return;
    controller.chartCells(dslSelectedCoords);
  }, [controller, dslSelectedCoords]);

  const sheetIsEmpty = useMemo(() => isSheetEmpty(safeConfig), [safeConfig]);

  const [populating, setPopulating] = useState(false);

  const handlePortfolioExtracted = useCallback(
    (snapshot: PortfolioSnapshot) => {
      setPopulating(true);
      controller.writeSheetCells(portfolioSheetCells(snapshot));
      setUploadOpen(false);
    },
    [controller],
  );

  // Every DSL cell has a final result — the uploaded sheet is fully rendered.
  const dslCellsSettled = useMemo(() => {
    for (let r = 0; r < safeConfig.rows; r += 1) {
      for (let c = 0; c < safeConfig.cols; c += 1) {
        if (!isDslCell(safeConfig.cells[r][c])) continue;
        const status = evaluations.get(coordKey(r, c))?.status;
        if (status !== 'ok' && status !== 'error') return false;
      }
    }
    return true;
  }, [safeConfig, evaluations]);

  useEffect(() => {
    if (populating && dslCellsSettled) setPopulating(false);
  }, [populating, dslCellsSettled]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const handleCellContextMenu = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>, r: number, c: number) => {
      event.preventDefault();
      const key = coordKey(r, c);
      if (!selected.has(key)) {
        setSelected(new Set([key]));
        setAnchor({ r, c });
        setActive({ r, c });
      }
      setMenu({ x: event.clientX, y: event.clientY });
    },
    [selected],
  );

  useEffect(() => {
    if (!menu) return;
    const onPointer = (event: MouseEvent) => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) return;
      closeMenu();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu, closeMenu]);

  const runMenuAction = useCallback(
    (fn: () => void) => () => {
      fn();
      closeMenu();
    },
    [closeMenu],
  );

  const handleMenuRefresh = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (event.shiftKey) controller.requestRetranslate();
      else controller.requestRefresh();
      closeMenu();
    },
    [controller, closeMenu],
  );

  const handleGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const inInput = target?.tagName === 'INPUT';
    if (
      (event.metaKey || event.ctrlKey) &&
      !event.shiftKey &&
      !event.altKey &&
      (event.key === 'z' || event.key === 'Z') &&
      !inInput
    ) {
      event.preventDefault();
      handleUndo();
      return;
    }
    if (
      (event.metaKey || event.ctrlKey) &&
      !event.shiftKey &&
      !event.altKey &&
      (event.key === 'a' || event.key === 'A') &&
      !inInput
    ) {
      event.preventDefault();
      handleSelectAllCells();
      return;
    }
    if (editingCell) return;
    if (inInput) return;
    if (!hasSelection) return;
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      handleDeleteSelected();
      return;
    }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      if (active) {
        setEditingCell({ ...active });
        setEditingInitialValue(null);
      }
      return;
    }
    if (
      active &&
      (event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      event.preventDefault();
      const lastRow = safeConfig.rows - 1;
      const lastCol = safeConfig.cols - 1;
      let r = active.r;
      let c = active.c;
      if (event.shiftKey) {
        switch (event.key) {
          case 'ArrowUp':
            if (r > 0) r -= 1;
            break;
          case 'ArrowDown':
            if (r < lastRow) r += 1;
            break;
          case 'ArrowLeft':
            if (c > 0) c -= 1;
            break;
          case 'ArrowRight':
            if (c < lastCol) c += 1;
            break;
        }
        if (r === active.r && c === active.c) return;
        const baseAnchor = anchor ?? active;
        const coords = rangeBetween(baseAnchor, { r, c });
        setSelected(
          new Set(coords.map(({ r: rr, c: cc }) => coordKey(rr, cc))),
        );
        if (!anchor) setAnchor({ ...active });
        setActive({ r, c });
        return;
      }
      switch (event.key) {
        case 'ArrowUp':
          if (r > 0) r -= 1;
          break;
        case 'ArrowDown':
          if (r < lastRow) r += 1;
          break;
        case 'ArrowLeft':
          if (c > 0) c -= 1;
          else if (r > 0) {
            r -= 1;
            c = lastCol;
          }
          break;
        case 'ArrowRight':
          if (c < lastCol) c += 1;
          else if (r < lastRow) {
            r += 1;
            c = 0;
          }
          break;
      }
      if (r === active.r && c === active.c) return;
      setSelected(new Set([coordKey(r, c)]));
      setAnchor({ r, c });
      setActive({ r, c });
      return;
    }
    if (
      selected.size === 1 &&
      active &&
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      setEditingCell({ ...active });
      setEditingInitialValue(event.key);
    }
  };

  return (
    <div
      className={[styles.container, populating ? styles.populating : '']
        .filter(Boolean)
        .join(' ')}
      data-testid="excel-widget"
      data-populating={populating ? 'true' : 'false'}
      data-instance-id={instanceId}
      onMouseDown={dropNativeSelection}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => controller.addRow()}
          disabled={safeConfig.rows >= MAX_EXCEL_ROWS}
          data-testid="excel-add-row"
          aria-label="Add row"
        >
          <Plus size={12} aria-hidden="true" />
          <span>Row</span>
        </button>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => controller.removeRow()}
          disabled={safeConfig.rows <= 1}
          data-testid="excel-remove-row"
          aria-label="Remove row"
        >
          <Minus size={12} aria-hidden="true" />
          <span>Row</span>
        </button>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => controller.addCol()}
          disabled={safeConfig.cols >= MAX_EXCEL_COLS}
          data-testid="excel-add-col"
          aria-label="Add column"
        >
          <Plus size={12} aria-hidden="true" />
          <span>Col</span>
        </button>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => controller.removeCol()}
          disabled={safeConfig.cols <= 1}
          data-testid="excel-remove-col"
          aria-label="Remove column"
        >
          <Minus size={12} aria-hidden="true" />
          <span>Col</span>
        </button>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => setUploadOpen(true)}
          disabled={!sheetIsEmpty}
          data-testid="excel-upload"
          aria-label="Upload your Portfolio"
          title={
            sheetIsEmpty
              ? 'Upload your Portfolio'
              : 'Upload needs an empty sheet — clear the cells first'
          }
        >
          <Upload size={12} aria-hidden="true" />
          <span>Upload</span>
        </button>
        <button
          ref={promptButtonRef}
          type="button"
          className={styles.promptButton}
          onClick={() => (promptOpen ? closePrompt() : setPromptOpen(true))}
          aria-expanded={promptOpen}
          data-testid="excel-prompt"
          aria-label="Open prompt"
          title="Prompt"
        >
          <Sparkles size={14} aria-hidden="true" />
        </button>
        <span className={styles.headerSpacer} />
        <button
          type="button"
          className={[
            styles.headerButton,
            controller.pinned ? styles.headerButtonActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => controller.togglePin()}
          aria-pressed={controller.pinned}
          data-testid="excel-toggle-pin"
          aria-label={controller.pinned ? 'Unpin widget' : 'Pin widget'}
          title={
            controller.pinned
              ? 'Unpin — allow moving and resizing'
              : 'Pin — lock position and size'
          }
        >
          {controller.pinned ? (
            <PinOff size={12} aria-hidden="true" />
          ) : (
            <Pin size={12} aria-hidden="true" />
          )}
          <span>{controller.pinned ? 'Pinned' : 'Pin'}</span>
        </button>
        <span className={styles.dimensions} data-testid="excel-dimensions">
          {safeConfig.rows} × {safeConfig.cols}
        </span>
      </div>

      <div
        ref={gridRef}
        className={styles.scroll}
        tabIndex={0}
        role="grid"
        onKeyDown={handleGridKeyDown}
        data-testid="excel-grid"
      >
        <table className={styles.table}>
          <thead>
            <tr data-testid="excel-header-row">
              <th
                className={`${styles.headerCell} ${styles.cornerCell}`}
                data-testid="excel-corner"
                aria-hidden="true"
              />
              {Array.from({ length: safeConfig.cols }, (_, c) => (
                <th
                  key={c}
                  className={`${styles.headerCell} ${styles.colHeader}`}
                  data-testid={`excel-col-header-${c}`}
                  scope="col"
                >
                  {colName(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeConfig.cells.map((row, r) => (
              <tr key={r} data-testid={`excel-row-${r}`}>
                <th
                  className={`${styles.headerCell} ${styles.rowHeader}`}
                  data-testid={`excel-rowhdr-${r}`}
                  scope="row"
                >
                  {r + 1}
                </th>
                {row.map((cell, c) => {
                  const dsl = isDslCell(cell);
                  const evaluation = evaluations.get(coordKey(r, c));
                  const cellOverlay = liveOverlay.get(coordKey(r, c));
                  const key = coordKey(r, c);
                  const isSelected = selected.has(key);
                  const isEditing =
                    editingCell !== null &&
                    editingCell.r === r &&
                    editingCell.c === c;
                  const activeCfStyle =
                    cell.conditionalFormatting && cell.conditionalFormatting.length > 0
                      ? memoEvaluateRules(
                          cell.conditionalFormatting,
                          numericValueOf(cell, evaluation, cellOverlay),
                        )
                      : null;
                  const effectiveBg = activeCfStyle?.bgColor ?? cell.bgColor;
                  const effectiveText = activeCfStyle?.textColor ?? cell.textColor;
                  const tdClass = [
                    styles.td,
                    isSelected ? styles.selected : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <td
                      key={c}
                      data-testid={`excel-td-${r}-${c}`}
                      data-cell-kind={dsl ? 'dsl' : 'literal'}
                      data-cell-status={evaluation?.status ?? 'idle'}
                      data-cell-selected={isSelected ? 'true' : 'false'}
                      data-cell-cf-rule={activeCfStyle?.ruleId ?? undefined}
                      className={tdClass}
                      style={
                        effectiveBg ? { backgroundColor: effectiveBg } : undefined
                      }
                      onMouseDown={(event) =>
                        handleCellMouseDown(event, r, c)
                      }
                      onDoubleClick={() => handleCellDoubleClick(r, c)}
                      onContextMenu={(event) =>
                        handleCellContextMenu(event, r, c)
                      }
                    >
                      <ExcelCellView
                        r={r}
                        c={c}
                        raw={cell.raw}
                        isDsl={dsl}
                        evaluation={evaluation}
                        liveOverlay={cellOverlay}
                        editing={isEditing}
                        initialValueOverride={
                          isEditing ? editingInitialValue : null
                        }
                        textColor={effectiveText}
                        numberFormat={cell.numberFormat}
                        onCommit={handleCellCommit}
                        onExitEdit={exitEdit}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {menu ? (
        <div
          ref={menuRef}
          className={styles.contextMenu}
          style={{ top: menu.y, left: menu.x }}
          role="menu"
          data-testid="excel-context-menu"
          onContextMenu={(event) => event.preventDefault()}
        >
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={runMenuAction(handleUndo)}
            disabled={!controller.canUndo}
            data-testid="excel-menu-undo"
          >
            <Undo2 size={16} aria-hidden="true" />
            <span>Undo</span>
            <span className={styles.contextMenuShortcut}>Ctrl+Z</span>
          </button>
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={runMenuAction(handleDeleteSelected)}
            disabled={!hasSelection}
            data-testid="excel-menu-delete"
          >
            <Trash2 size={16} aria-hidden="true" />
            <span>Clear values</span>
            <span className={styles.contextMenuShortcut}>Del</span>
          </button>
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={runMenuAction(handleChart)}
            disabled={!hasDslSelection}
            data-testid="excel-menu-chart"
          >
            <LineChart size={16} aria-hidden="true" />
            <span>Chart selected DSL</span>
          </button>
          <div className={styles.contextMenuSeparator} aria-hidden="true" />
          <div className={styles.contextMenuColorRow}>
            <span className={styles.contextMenuColorLabel}>
              <Type size={16} aria-hidden="true" />
              <span>Text</span>
            </span>
            <div
              className={styles.contextMenuPalette}
              role="group"
              aria-label="Text color"
            >
              {COLOR_PALETTE.map((color) => (
                <button
                  key={`text-${color}`}
                  type="button"
                  className={styles.contextMenuPaletteSwatch}
                  style={{ backgroundColor: color }}
                  aria-label={color}
                  title={color}
                  disabled={!hasSelection}
                  onClick={runMenuAction(() => handleTextColor(color))}
                  data-testid={`excel-menu-text-color-${color}`}
                />
              ))}
            </div>
            <button
              type="button"
              className={styles.contextMenuColorReset}
              onClick={runMenuAction(handleResetTextColor)}
              disabled={!hasSelection}
              aria-label="Reset text color"
              title="Reset text color"
              data-testid="excel-menu-text-color-reset"
            >
              <RotateCcw size={13} aria-hidden="true" />
            </button>
          </div>
          <div className={styles.contextMenuColorRow}>
            <span className={styles.contextMenuColorLabel}>
              <PaintBucket size={16} aria-hidden="true" />
              <span>Fill</span>
            </span>
            <div
              className={styles.contextMenuPalette}
              role="group"
              aria-label="Background color"
            >
              {COLOR_PALETTE.map((color) => (
                <button
                  key={`bg-${color}`}
                  type="button"
                  className={styles.contextMenuPaletteSwatch}
                  style={{ backgroundColor: color }}
                  aria-label={color}
                  title={color}
                  disabled={!hasSelection}
                  onClick={runMenuAction(() => handleBgColor(color))}
                  data-testid={`excel-menu-bg-color-${color}`}
                />
              ))}
            </div>
            <button
              type="button"
              className={styles.contextMenuColorReset}
              onClick={runMenuAction(handleResetBgColor)}
              disabled={!hasSelection}
              aria-label="Reset background color"
              title="Reset background color"
              data-testid="excel-menu-bg-color-reset"
            >
              <RotateCcw size={13} aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={runMenuAction(handleClearFormatting)}
            disabled={!hasSelection}
            data-testid="excel-menu-clear-format"
          >
            <span>Clear formatting</span>
          </button>
          <div className={styles.contextMenuSeparator} aria-hidden="true" />
          <div className={styles.contextMenuFormatRow}>
            <span className={styles.contextMenuColorLabel}>
              <Hash size={16} aria-hidden="true" />
              <span>Format</span>
            </span>
            <span
              className={styles.contextMenuFormatCurrent}
              data-testid="excel-menu-format-current"
            >
              {selectedFormat === undefined
                ? 'Mixed'
                : describeFormat(selectedFormat ?? undefined)}
            </span>
          </div>
          <div className={styles.contextMenuFormatChips} role="group" aria-label="Cell format">
            <button
              type="button"
              className={`${styles.contextMenuChip} ${
                selectedFormat &&
                selectedFormat !== null &&
                selectedFormat.kind === 'percent'
                  ? styles.contextMenuChipActive
                  : ''
              }`}
              disabled={!hasSelection}
              onClick={runMenuAction(() => handleSetFormat({ kind: 'percent' }))}
              data-testid="excel-menu-format-percent"
              title="Percent — multiplies numeric value by 100 and appends %"
            >
              %
            </button>
            <button
              type="button"
              className={`${styles.contextMenuChip} ${
                selectedFormat &&
                selectedFormat !== null &&
                selectedFormat.kind === 'text'
                  ? styles.contextMenuChipActive
                  : ''
              }`}
              disabled={!hasSelection}
              onClick={runMenuAction(() => handleSetFormat({ kind: 'text' }))}
              data-testid="excel-menu-format-text"
              title="Text — render value as plain text"
            >
              Text
            </button>
            <button
              type="button"
              className={`${styles.contextMenuChip} ${
                selectedFormat &&
                selectedFormat !== null &&
                selectedFormat.kind === 'date'
                  ? styles.contextMenuChipActive
                  : ''
              }`}
              disabled={!hasSelection}
              onClick={runMenuAction(() => handleSetFormat({ kind: 'date' }))}
              data-testid="excel-menu-format-date"
              title="Date — parse value as date and render in locale format"
            >
              Date
            </button>
            <button
              type="button"
              className={styles.contextMenuChip}
              disabled={!hasSelection || !selectedFormat}
              onClick={runMenuAction(handleClearFormat)}
              data-testid="excel-menu-format-clear"
              title="Clear cell format"
            >
              <RotateCcw size={13} aria-hidden="true" />
            </button>
          </div>
          <div className={styles.contextMenuFormatRow}>
            <span className={styles.contextMenuColorLabel}>
              <span>Decimal</span>
            </span>
            <div
              className={styles.contextMenuFormatChips}
              role="group"
              aria-label="Decimal digits"
            >
              {DECIMAL_DIGIT_OPTIONS.map((digits) => (
                <button
                  key={`decimal-${digits}`}
                  type="button"
                  className={`${styles.contextMenuChip} ${
                    selectedFormat &&
                    selectedFormat !== null &&
                    selectedFormat.kind === 'decimal' &&
                    selectedFormat.digits === digits
                      ? styles.contextMenuChipActive
                      : ''
                  }`}
                  disabled={
                    !hasSelection ||
                    digits < MIN_DECIMAL_DIGITS ||
                    digits > MAX_DECIMAL_DIGITS
                  }
                  onClick={runMenuAction(() =>
                    handleSetFormat({ kind: 'decimal', digits }),
                  )}
                  data-testid={`excel-menu-format-decimal-${digits}`}
                  title={`Decimal with ${digits} digit${digits === 1 ? '' : 's'}`}
                >
                  {digits}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.contextMenuSeparator} aria-hidden="true" />
          <div className={styles.contextMenuSeparator} aria-hidden="true" />
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={() => {
              if (menu && hasSelection) {
                setCfPopover({ x: menu.x, y: menu.y });
              }
              closeMenu();
            }}
            disabled={!hasSelection}
            data-testid="excel-menu-conditional-formatting"
            title="Add rules that color this cell based on its value"
          >
            <Wand2 size={16} aria-hidden="true" />
            <span>Conditional formatting…</span>
          </button>
          <div className={styles.contextMenuSeparator} aria-hidden="true" />
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={handleMenuRefresh}
            data-testid="excel-menu-refresh"
            title="Refresh values. Shift+click also re-runs the DSL agent."
          >
            <RefreshCw size={16} aria-hidden="true" />
            <span>Refresh</span>
            <span className={styles.contextMenuShortcut}>⇧ retranslate</span>
          </button>
        </div>
      ) : null}

      {cfPopover ? (
        <ConditionalFormattingPopover
          anchor={cfPopover}
          initialRules={cfSelection.rules}
          isMixed={cfSelection.isMixed}
          onApply={handleCfApply}
          onCancel={handleCfCancel}
        />
      ) : null}

      {promptOpen && promptButtonRef.current ? (
        <ExcelPromptPopover
          anchorEl={promptButtonRef.current}
          busy={promptBusy}
          error={promptError}
          onSubmit={handlePromptSubmit}
          onClose={closePrompt}
        />
      ) : null}

      <ExcelUploadModal
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onExtracted={handlePortfolioExtracted}
      />
    </div>
  );
};

export default ExcelWidget;
